import { useState, useEffect, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { MessageContext } from '../context/MessageContext';
import { AuthContext } from '../context/AuthContext';
import YearPicker from '../components/YearPicker';
import DatePicker from '../components/DatePicker';
import Icons from '../components/Icons';
import FileUpload from '../components/PhotoUpload';
import { format } from 'date-fns';
import axios from 'axios';

const CreateCarOffer = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, serverNotAvailable, loading: authLoading } = useContext(AuthContext);
  const { setMess } = useContext(MessageContext);

  const [currencies, setCurrencies] = useState([]);
  const [cities, setCities] = useState([]);
  const [selectedCustomsCity, setSelectedCustomsCity] = useState([]);
  const [selectedBorderCity, setSelectedBorderCity] = useState([]);

  const [newPhotos, setNewPhotos] = useState([]);
  const [existingPhotos, setExistingPhotos] = useState([]);
  const [photosToDelete, setPhotosToDelete] = useState([]);

  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);

  const totalFiles = existingPhotos.length + newPhotos.length;
  const isLimitExceeded = totalFiles > process.env.REACT_APP_MAX_UPLOAD_FILES;

  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState({
    id_car_request: location.state?.id_car_request,
    description: location.state?.description,
    valid_until: location.state?.valid_until ? format(location.state.valid_until, 'dd.MM.yyyy') : format(new Date(Date.now() + 7 * 24 * 60 * 60 * 1000), 'dd.MM.yyyy'),
    price: location.state?.max_price,
    id_currency: location.state?.id_currency || '1',
    year: location.state?.year ? new Date(location.state.year).getFullYear().toString() : (new Date().getFullYear() - 3).toString(),
    mileage: location.state?.mileage,
    customs_cities: location.state?.customs_cities || [],
    border_cities: location.state?.border_cities || []
  });

  useEffect(() => {
    if (location.state?.id_car_offer) {
      setIsEditing(true);
    }
    setSelectedCustomsCity(cities.filter(city => location?.state?.customs_cities?.includes(city.id)));
    setSelectedBorderCity(cities.filter(city => location?.state?.border_cities?.includes(city.id)));
  }, [cities]);

  useEffect(() => {
    if (!user?.id) return;
    const fetchCitiesAndCurrencies = async () => {
      try {
        const city_curr = await axios.get('/api/cities_currencies');
        setCurrencies(city_curr.data.currencies);
        setCities(city_curr.data.cities);
      } catch (err) {
        setMess({ color: 'red', title: 'Ошибка загрузки данных:', desc: err.message });
      }
    };
    fetchCitiesAndCurrencies();
  }, [user, authLoading,]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleYearChange = (name, year) => {
    setFormData((prev) => ({ ...prev, [name]: year }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.description?.trim()) newErrors.description = 'Описание обязательно';
    if (!formData.price) newErrors.price = 'Цена обязательна';
    if (!formData.year) newErrors.year = 'Дата выпуска обязательна';
    if (!formData.mileage) newErrors.mileage = 'Пробег обязателен';
    if (formData.customs_cities.length < 1) newErrors.customs_cities = 'Выберите город';
    if (formData.border_cities.length < 1) newErrors.border_cities = 'Выберите город';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      setMess({ color: 'red', title: 'Ошибка валидации:', desc: 'Заполните все обязательные поля' });
      return;
    }

    setLoading(true);
    try {
      const uploadFormData = new FormData();
      // Добавляем данные предложения
      uploadFormData.append('id_car_request', formData.id_car_request);
      uploadFormData.append('description', formData.description);
      uploadFormData.append('price', formData.price);
      uploadFormData.append('id_currency', formData.id_currency);
      uploadFormData.append('mileage', formData.mileage);
      uploadFormData.append('valid_until', formData.valid_until.split('.').reverse().join('-'));
      uploadFormData.append('year', formData.year ? `${formData.year}-01-01` : '');
      uploadFormData.append('customs_cities', JSON.stringify(formData.customs_cities));
      uploadFormData.append('border_cities', JSON.stringify(formData.border_cities));

      // Добавляем новые фото
      newPhotos.forEach(photo => uploadFormData.append('photos', photo.file));

      // Для редактирования: добавляем IDs фото для удаления
      if (isEditing) {
        uploadFormData.append('photosToDelete', JSON.stringify(photosToDelete));
      }

      let carOfferResponse;
      if (isEditing) {
        carOfferResponse = await axios.put(`/api/car-offer/${location.state.id_car_offer}`, uploadFormData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      } else {
        carOfferResponse = await axios.post('/api/car-offer', uploadFormData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }

      // Успех: Обновляем состояние (удаляем deleted, очищаем newPhotos)
      if (isEditing) {
        setExistingPhotos(prev => prev.filter(p => !photosToDelete.includes(p.id)));
      }
      setNewPhotos([]);
      setPhotosToDelete([]);

      setMess({ color: 'green', title: 'Успешно:', desc: isEditing ? 'Предложение обновлено' : 'Предложение создано' });
      navigate('/carOffers');

    } catch (error) {
      if (error.response) {
        setMess({ color: 'red', title: 'Ошибка сохранения:', desc: (error.response.data?.message || error.response.data.error)});
      } else {
        setMess({ color: 'red', title: 'Ошибка:', desc: error.message });
      }
    } finally {
      setLoading(false);
    }
  };

  const MultiSelect = ({ label, options, value, onChange, placeholder = "Выберите...", name, errors = null }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [filter, setFilter] = useState("");
    const filteredOptions = options.filter(
      (city) => city.city_name.toLowerCase().includes(filter.toLowerCase())
    );

    const toggleOption = (city) => {
      if (value.includes(city)) {
        onChange(value.filter((c) => c !== city));
      } else {
        onChange([...value, city]);
      }
    };

    return (
      <div className="divPlaceholder relative w-1/2 ">
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className="peer ring-gray-300 dark:ring-gray-600 w-full px-4 py-2 bg-transparent rounded-md focus:outline-none ring-1 focus:ring-green-400 transition-all duration-200 placeholder-transparent"
        >
          {value.length > 0 ? (
            <span className="text-gray-800 dark:text-gray-200">
              {value.slice(0, 2).map(city => city.city_name || "Неизвестно").join(", ")}{value.length > 2 && (
                <sup className="ml-1 text-xs bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 px-1 rounded">
                  +{value.length - 2}
                </sup>
              )}
            </span>
          ) : (
            <span className="text-gray-500 dark:text-gray-400">{placeholder}</span>
          )}
        </button>
        <label className="absolute bg-white dark:bg-gray-800 rounded-t-lg cursor-pointer left-0 -top-3 text-sm text-gray-500 mx-1 px-1 peer-placeholder-shown:text-base peer-placeholder-shown:text-gray-500 peer-placeholder-shown:top-2 peer-focus:-top-4 peer-focus:text-green-600 peer-focus:text-sm transition-all duration-300 pointer-events-none">
          {label}
        </label>
        {isOpen && (
          <div className="absolute mt-1 w-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg z-10">
            <div className="p-2 border-b border-gray-200 dark:border-gray-700">
              <input
                type="text"
                placeholder="Поиск..."
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-green-500"
                autoFocus
              />
            </div>
            {filteredOptions.length === 0 ? (
              <div className="px-4 py-2 text-sm text-gray-500 dark:text-gray-400">Ничего не найдено</div>
            ) : (
              <ul className="h-[180px] overflow-y-auto">
                {filteredOptions.map((city) => (
                  <li key={city.id}>
                    <button
                      type="button"
                      onClick={() => toggleOption(city)}
                      className={`w-full text-left px-4 py-2 text-sm flex items-center justify-between hover:bg-gray-100 dark:hover:bg-gray-700 ${value.includes(city)
                        ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300"
                        : "text-gray-700 dark:text-gray-300"
                        }`}
                    >
                      <span>{city.city_name}</span>
                      {value.includes(city) && (
                        <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                        </svg>
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
        {errors && <p className="text-red-500 text-sm mt-1">{errors}</p>}
      </div>
    );
  };

  // Сообщение "Сервер не доступен"
  if (serverNotAvailable) return (
    <section className="py-10">
      <div className="flex justify-center px-4">
        <div className="flex w-full max-w-md bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-center w-12 bg-yellow-500">
            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div className="px-4 py-3">
            <div className="flex flex-col">
              <span className="textyellow-500 dark:text-yellow-400">Ошибка загрузки данных</span>
              <p className="text-sm text-gray-600 dark:text-gray-200">
                Сервер не доступен
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );

  if (authLoading) return <Icons name="Spinner" />;

  if (!user && !authLoading)
    return (
      <section className="py-10">
        <div className="flex justify-center px-4">
          <div className="flex w-full max-w-md bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-center w-12 bg-green-500">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="px-4 py-3">
              <div className="flex flex-col">
                <span className="text-green-500 dark:text-green-400">Требуется авторизация</span>
                <p className="text-sm text-gray-600 dark:text-gray-200">Пожалуйста, войдите в систему, чтобы продолжить.</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    );

  if (location.state?.model_name_en == null)
    return (
      <section className="py-10">
        <div className="flex justify-center px-4">
          <div className="flex w-full max-w-md bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-center w-12 bg-yellow-400">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="px-4 py-3">
              <div className="flex flex-col">
                <span className="text-yellow-400 dark:text-yellow-300">Не выбрана заявка</span>
                <p className="text-sm text-gray-600 dark:text-gray-200">Создайте предложение по кнопке "Создать предложение" на странице <a className='text-blue-500 hover:underline' href="/CarRequests">"Заявки"</a>.</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    );

  return (
    <section className="py-4 px-4 flex flex-col items-center">
      <div className="max-w-xl w-full bg-white dark:bg-gray-800 rounded-lg shadow-md px-3 py-4">
        <h2 className="text-xl mb-4 text-green-600 dark:text-green-400">{isEditing ? "Изменить" : "Создать"} предложение по заявке #{formData.id_car_request}</h2>
        {location.state.model_name_en && (
          <h2 className="text-xl mb-4 text-green-600 dark:text-green-400">{location.state?.model_name_en}</h2>
        )}
        <form onSubmit={handleSubmit} className="space-y-5">
          {/* ... поля формы ... */}
          <div className="divPlaceholder relative">
            <textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              className="peer ring-gray-300 dark:ring-gray-600 w-full px-4 py-2 bg-transparent rounded-md focus:outline-none ring-1 focus:ring-green-400 transition-all duration-200 placeholder-transparent"
              rows="3"
            ></textarea>
            <label className="absolute bg-white dark:bg-gray-800 rounded-t-lg cursor-pointer left-0 -top-3 text-sm text-gray-500 mx-1 px-1 peer-placeholder-shown:text-base peer-placeholder-shown:text-gray-500 peer-placeholder-shown:top-2 peer-focus:-top-4 peer-focus:text-green-600 peer-focus:text-sm transition-all duration-300 pointer-events-none">Описание</label>
            {errors.description && <p className="text-red-500 text-sm mt-1">{errors.description}</p>}
          </div>

          <div className="flex gap-4">
            <div className="divPlaceholder relative w-1/2">
              <YearPicker nameInput="year" valYearInput={formData.year} placeholderInput="Год выпуска" onYearChange={handleYearChange} />
              {errors.year && <p className="text-red-500 text-sm mt-1">{errors.year}</p>}
            </div>
            <div className="divPlaceholder relative w-1/2">
              <DatePicker nameInput="valid_until" valDateInput={formData.valid_until} placeholderInput="Действительно до" onDateChange={handleYearChange} />
            </div>
          </div>

          <div className="flex gap-4">
            <div className="divPlaceholder relative w-1/2">
              <input
                type="number"
                name="mileage"
                value={formData.mileage}
                onChange={handleInputChange}
                className="peer ring-gray-300 dark:ring-gray-600 w-full px-4 py-2 bg-transparent rounded-md focus:outline-none ring-1 focus:ring-green-400 transition-all duration-200 placeholder-transparent"
              />
              <label className="absolute bg-white dark:bg-gray-800 rounded-t-lg cursor-pointer left-0 -top-3 text-sm text-gray-500 mx-1 px-1 peer-placeholder-shown:text-base peer-placeholder-shown:text-gray-500 peer-placeholder-shown:top-2 peer-focus:-top-4 peer-focus:text-green-600 peer-focus:text-sm transition-all duration-300 pointer-events-none">Пробег, км</label>
              {errors.mileage && <p className="text-red-500 text-sm mt-1">{errors.mileage}</p>}
            </div>
            <div className="divPlaceholder relative w-1/2">
              <input
                type="number"
                name="price"
                value={formData.price}
                onChange={handleInputChange}
                className="peer ring-gray-300 dark:ring-gray-600 w-full px-4 py-2 bg-transparent rounded-md focus:outline-none ring-1 focus:ring-green-400 transition-all duration-200 placeholder-transparent"
              />
              <label className="absolute bg-white dark:bg-gray-800 rounded-t-lg cursor-pointer left-0 -top-3 text-sm text-gray-500 mx-1 px-1 peer-placeholder-shown:text-base peer-placeholder-shown:text-gray-500 peer-placeholder-shown:top-2 peer-focus:-top-4 peer-focus:text-green-600 peer-focus:text-sm transition-all duration-300 pointer-events-none">Цена</label>
              <select
                name="id_currency"
                value={formData.id_currency}
                onChange={handleInputChange}
                className="absolute right-0 z-2 text-sm sm:text-base px-1 py-2 rounded-r-md bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-800 h-10"
              >
                {currencies.map((currency) => (
                  <option key={currency.id_currency} value={currency.id_currency}>
                    {currency.name_curr}
                  </option>
                ))}
              </select>
              {errors.price && <p className="text-red-500 text-sm mt-1">{errors.price}</p>}
            </div>
          </div>

          <div className="flex gap-4">
            <MultiSelect
              name="customs_cities"
              label="Город на таможне"
              options={cities}
              value={selectedCustomsCity}
              onChange={(newValues) => {
                setFormData(prev => ({ ...prev, customs_cities: newValues.map(city => city.id) }));
                setSelectedCustomsCity(newValues);
              }}
              placeholder="Выбрать"
              errors={errors.customs_cities}
            />
            <MultiSelect
              name="border_cities"
              label="Город на границе"
              options={cities}
              value={selectedBorderCity}
              onChange={(newValues) => {
                setFormData(prev => ({ ...prev, border_cities: newValues.map(city => city.id) }));
                setSelectedBorderCity(newValues);
              }}
              placeholder="Выбрать"
              errors={errors.border_cities}
            />
          </div>

          {/* УПРАВЛЕНИЕ ФОТО */}
          <FileUpload
            toLoadFiles={isEditing}
            idCarOffer={location.state.id_car_offer}
            newFiles={newPhotos}
            setNewFiles={setNewPhotos}
            existingFiles={existingPhotos}
            setExistingFiles={setExistingPhotos}
            setFilesToDelete={setPhotosToDelete}
          />

          {/* Кнопка сохранения */}
          <button
            type="submit"
            disabled={loading || isLimitExceeded}
            className={`w-full py-3 px-4 text-white rounded-md transition-colors ${isLimitExceeded ? 'bg-gray-500 cursor-not-allowed' : 'buttonGreen'}`}
          >
            {loading
              ? (
                <>
                  <div className="space-x-1">
                    <span>Сохранение</span>
                    <span className="fade-1">.</span>
                    <span className="fade-2">.</span>
                    <span className="fade-3">.</span>
                    <span>Не закрывайте страницу.</span>
                  </div>
                </>
              )
              : 'Сохранить'}
          </button>
        </form>
      </div>
    </section>
  );
};

export default CreateCarOffer;